import { LocalDate, ZoneId } from "@js-joda/core";
import "@js-joda/timezone";

export type DatePickerDateFormat = "YYYY-MM-DD" | "DD/MM/YYYY" | "MM/DD/YYYY";
export type DatePickerTranslate = (key: string) => string;

export interface DatePickerCalendarDay {
  date: LocalDate;
  isOtherMonth: boolean;
}

export interface DatePickerCalendarLabels {
  monthNames: string[];
  dayHeaders: string[];
}

const STOCKHOLM_TIME_ZONE = ZoneId.of("Europe/Stockholm");

/** Returns the current date in Stockholm local time. */
export const getStockholmLocalDateNow = (): LocalDate =>
  LocalDate.now(STOCKHOLM_TIME_ZONE);

/** Returns a new date normalized to the first day of the month. */
export const normalizeToFirstOfMonth = (base: LocalDate): LocalDate =>
  LocalDate.of(base.year(), base.monthValue(), 1);

/** Returns translated month names and weekday headers for calendar UI. */
export const getCalendarLabels = (
  t: DatePickerTranslate,
): DatePickerCalendarLabels => ({
  monthNames: [
    t("OrganizationalStructure.January"),
    t("OrganizationalStructure.February"),
    t("OrganizationalStructure.March"),
    t("OrganizationalStructure.April"),
    t("OrganizationalStructure.May"),
    t("OrganizationalStructure.June"),
    t("OrganizationalStructure.July"),
    t("OrganizationalStructure.August"),
    t("OrganizationalStructure.September"),
    t("OrganizationalStructure.October"),
    t("OrganizationalStructure.November"),
    t("OrganizationalStructure.December"),
  ],
  dayHeaders: [
    t("OrganizationalStructure.Mo"),
    t("OrganizationalStructure.Tu"),
    t("OrganizationalStructure.We"),
    t("OrganizationalStructure.Th"),
    t("OrganizationalStructure.Fr"),
    t("OrganizationalStructure.Sa"),
    t("OrganizationalStructure.Su"),
  ],
});

/** Builds a 6x7 calendar grid for the month that contains `base`. */
export const buildCalendarDays = (base: LocalDate): DatePickerCalendarDay[] => {
  const year = base.year();
  const month = base.monthValue();
  const firstDay = LocalDate.of(year, month, 1);
  const daysInMonth = firstDay.lengthOfMonth();
  const startingDayOfWeek = firstDay.dayOfWeek().value();
  const mondayBasedStartDay = startingDayOfWeek - 1;
  const out: DatePickerCalendarDay[] = [];
  const prevMonth =
    month === 1
      ? LocalDate.of(year - 1, 12, 1)
      : LocalDate.of(year, month - 1, 1);
  const prevMonthLength = prevMonth.lengthOfMonth();

  for (let index = mondayBasedStartDay - 1; index >= 0; index--) {
    out.push({
      date: LocalDate.of(
        prevMonth.year(),
        prevMonth.monthValue(),
        prevMonthLength - index,
      ),
      isOtherMonth: true,
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    out.push({ date: LocalDate.of(year, month, day), isOtherMonth: false });
  }

  const remaining = 42 - out.length;
  const nextMonth =
    month === 12
      ? LocalDate.of(year + 1, 1, 1)
      : LocalDate.of(year, month + 1, 1);
  for (let day = 1; day <= remaining; day++) {
    out.push({
      date: LocalDate.of(nextMonth.year(), nextMonth.monthValue(), day),
      isOtherMonth: true,
    });
  }

  return out;
};

/** Formats a date as a day key (YYYY-MM-DD). */
export const toDayKey = (date: LocalDate): string => date.toString();

/** Parses a day key (YYYY-MM-DD) into a LocalDate object. */
export const parseDayKey = (key: string): LocalDate => LocalDate.parse(key);

/** Checks if two dates share the same year and month. */
export const isSameMonthYear = (a: LocalDate, b: LocalDate): boolean =>
  a.year() === b.year() && a.monthValue() === b.monthValue();

/** Formats a date using the provided DatePicker format. */
export const formatDateValue = (
  date: LocalDate | null,
  format: DatePickerDateFormat,
): string => {
  if (!date) return "";
  const y = date.year();
  const m = String(date.monthValue()).padStart(2, "0");
  const d = String(date.dayOfMonth()).padStart(2, "0");
  switch (format) {
    case "DD/MM/YYYY":
      return `${d}/${m}/${y}`;
    case "MM/DD/YYYY":
      return `${m}/${d}/${y}`;
    default:
      return `${y}-${m}-${d}`;
  }
};

/** Builds a day-by-day array from start to end (inclusive). */
export const buildDateArray = (
  start: LocalDate,
  end: LocalDate,
): LocalDate[] => {
  const arr: LocalDate[] = [];
  let cursor = start;
  while (!cursor.isAfter(end)) {
    arr.push(cursor);
    cursor = cursor.plusDays(1);
  }
  return arr;
};

/** Returns true when the date is Saturday or Sunday. */
export const isWeekend = (d: LocalDate): boolean => {
  const dow = d.dayOfWeek().value();
  return dow === 6 || dow === 7;
};

/** Parses a user input string into a LocalDate based on the specified format. */
export const parseInputDate = (
  input: string,
  dateFormat: DatePickerDateFormat,
): LocalDate | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/[./-]/).map((part) => part.trim());
  let year = 0;
  let month = 0;
  let day = 0;
  if (dateFormat === "DD/MM/YYYY") {
    if (parts.length !== 3) return null;
    day = Number(parts[0]);
    month = Number(parts[1]);
    year = Number(parts[2]);
  } else if (dateFormat === "MM/DD/YYYY") {
    if (parts.length !== 3) return null;
    month = Number(parts[0]);
    day = Number(parts[1]);
    year = Number(parts[2]);
  } else {
    if (parts.length !== 3) return null;
    year = Number(parts[0]);
    month = Number(parts[1]);
    day = Number(parts[2]);
  }

  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    year < 1000
  ) {
    return null;
  }

  try {
    return LocalDate.of(year, month, day);
  } catch {
    return null;
  }
};

/** Formats a multi-date selection for display. */
export const formatMultiDateDisplay = (
  keys: Set<string>,
  dateFormat: DatePickerDateFormat,
): string => {
  if (keys.size === 0) return "";
  const dates = Array.from(keys)
    .map(parseDayKey)
    .sort((a, b) => a.compareTo(b));
  const formatted = dates.map((d) => formatDateValue(d, dateFormat));
  if (formatted.length <= 3) return formatted.join(", ");
  return `${formatted.slice(0, 3).join(", ")} +${formatted.length - 3}`;
};
